import { useNavigate } from "react-router-dom";

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { useFormSafety } from "@/lib/navigation/useFormSafety";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { DiscardChangesDialog } from "@a2z/ui";

type DirtySource = "details" | "content" | "variants" | "images";

type NavigationGuardContextValue = {
  isDirty: boolean;
  promptDiscard: (action?: () => void) => void;
  setDirtySource: (source: DirtySource, isDirty: boolean) => void;
};

const NavigationGuardContext = createContext<NavigationGuardContextValue | null>(
  null,
);

export function NavigationGuardProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const navigate = useNavigate();
  const t = useTranslations();
  const [dirtySources, setDirtySources] = useState<Record<DirtySource, boolean>>({
    details: false,
    content: false,
    variants: false,
    images: false,
  });

  const isDirty =
    dirtySources.details ||
    dirtySources.content ||
    dirtySources.variants ||
    dirtySources.images;
  const { promptDiscard, discardDialogState } = useFormSafety({
    isDirty,
    enableNavigationGuard: true,
  });

  const setDirtySource = useCallback(
    (source: DirtySource, nextDirty: boolean) => {
      setDirtySources((current) => {
        if (current[source] === nextDirty) {
          return current;
        }
        return { ...current, [source]: nextDirty };
      });
    },
    [],
  );

  useEffect(() => {
    if (!isDirty || typeof window === "undefined") {
      return;
    }

    const handleClick = (event: MouseEvent) => {
      if (event.defaultPrevented) {
        return;
      }
      if (event.button !== 0) {
        return;
      }
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      const target = event.target as HTMLElement | null;
      const anchor = target?.closest("a");
      if (!anchor) {
        return;
      }
      if (anchor.target === "_blank" || anchor.hasAttribute("download")) {
        return;
      }
      const href = anchor.getAttribute("href");
      if (
        !href ||
        href.startsWith("#") ||
        href.startsWith("mailto:") ||
        href.startsWith("tel:")
      ) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();

      if (/^https?:\/\//i.test(href)) {
        promptDiscard(() => {
          window.location.assign(href);
        });
        return;
      }

      promptDiscard(() => {
        navigate(href);
      });
    };

    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, [isDirty, promptDiscard, navigate]);

  const value = useMemo(
    () => ({ isDirty, promptDiscard, setDirtySource }),
    [isDirty, promptDiscard, setDirtySource],
  );

  return (
    <NavigationGuardContext.Provider value={value}>
      {children}
      <DiscardChangesDialog
        {...discardDialogState}
        title={t(keys.forms.discardChangesTitle)}
        body={t(keys.forms.discardChangesBody)}
        discardLabel={t(keys.forms.discardChangesDiscard)}
        continueLabel={t(keys.forms.discardChangesContinue)}
      />
    </NavigationGuardContext.Provider>
  );
}

export function useNavigationGuard() {
  return useContext(NavigationGuardContext);
}
